import { ErrorIntegration } from "@features/tickets";
import { AlertCircle, RefreshCw } from "lucide-react";
import { cn } from "@/lib/utils/cn";
import { errorHandler } from "@/lib/utils/errorHandler";

/**
 * Error fallback component for displaying errors
 * @param {Object} props - Component props
 * @param {Error} props.error - The error object
 * @param {Function} props.resetErrorBoundary - Function to reset the error boundary
 * @param {string} props.className - Additional CSS classes
 * @param {string} props.title - Optional custom title
 */
export function ErrorFallback({
  error,
  resetErrorBoundary,
  className,
  title = "Une erreur est survenue",
}) {
  const userMessage = errorHandler.getUserMessage(error);

  return (
    <div
      className={cn(
        "flex flex-col items-center justify-center p-8 bg-error/5 rounded-lg border border-error/20",
        className,
      )}
    >
      <AlertCircle className="w-16 h-16 text-error/70 mb-4" strokeWidth={1.5} />

      <h2 className="text-xl font-bold text-error mb-2">{title}</h2>

      <p className="text-error/80 text-center mb-4 max-w-md">{userMessage}</p>

      {/* Show technical details only in development */}
      {import.meta.env.DEV && error.stack && (
        <details className="mt-4 w-full max-w-2xl">
          <summary className="cursor-pointer text-sm text-error/70 hover:text-error">
            Détails techniques
          </summary>
          <pre className="mt-2 p-4 bg-base-200 rounded text-xs overflow-auto max-h-60">
            {error.stack}
          </pre>
        </details>
      )}

      {resetErrorBoundary && (
        <button onClick={resetErrorBoundary} className="btn btn-error btn-sm mt-6 gap-2">
          <RefreshCw className="w-4 h-4" />
          Réessayer
        </button>
      )}

      <ErrorIntegration statusCode={error.response?.status} errorMessage={userMessage} />
    </div>
  );
}
